import { useQuery } from '@apollo/client'
import { useEffect, useMemo } from 'react'

import { gql } from '@/clients/utoniq-core'
import {
  StoreCollectionDetailsForGuestQuery,
  StoreCollectionDetailsForGuestQueryVariables,
  StoreCollectionDetailsQuery,
  StoreCollectionDetailsQueryVariables,
  StoreCollectionItemsConnectionFragmentDoc,
  UserCartsConnectionFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useAuth, useMessage, useRouter, useTranslation } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { makeViewModel } from '@/core/architecture'
import { App } from '@/core/native'
import { Share } from '@/core/native'
import { useCreateShortLink } from '@/lib/url/shortLink'

import { Action, State } from './template'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { t } = useTranslation()
  const { showError } = useMessage()
  const { analytics } = useAnalytics()
  const { router, generatePath, routerPages } = useRouter<{
    collectionId: string
    artistId: string
  }>()
  const { user: currentUser } = useAuth()

  const artistId =
    config.applicationType === 'standalone'
      ? config.artistId
      : router.query.artistId

  const { data, loading, refetch, fetchMore } = useQuery<
    StoreCollectionDetailsQuery,
    StoreCollectionDetailsQueryVariables
  >(QUERY, {
    variables: {
      collectionId: router.query.collectionId,
      collectionItemsFirstLabel: true,
      pageInput: {
        limit: 12,
      },
    },
  })

  const {
    data: dataForGuest,
    loading: loadingForGuest,
    refetch: refetchForGuest,
    fetchMore: fetchMoreForGuest,
  } = useQuery<
    StoreCollectionDetailsForGuestQuery,
    StoreCollectionDetailsForGuestQueryVariables
  >(QUERY_FOR_GUEST, {
    variables: {
      collectionId: router.query.collectionId,
      collectionItemsFirstLabel: true,
      pageInput: {
        limit: 12,
      },
    },
  })

  const hasData = useMemo(
    () => !!(data ?? dataForGuest)?.collection,
    [data, dataForGuest]
  )

  useEffect(() => {
    if (!hasData) return
    analytics?.track('view_item_list', {
      item_list_id: `collection_store_list_${router.query.collectionId}`,
      item_list_name: 'Collection Store Items',
      items: (data ?? dataForGuest)?.collection?.collectionItems.edges.map(
        ({ node }) => ({
          item_id: node.item.id,
          item_name: node.item.title,
          item_category: 'store_item',
          item_category2: node.item.type,
          item_category3: App.getPlatform(),
          item_category4: 1,
          item_category5: router.query.artistId,
          price: node.item.price,
        })
      ),
    })
  }, [hasData])

  const getShortLinkUrl = useCreateShortLink({
    path: generatePath(routerPages.StoreCollectionDetail, {
      ...router.query,
    }),
  })

  return {
    state: {
      loading: loading || loadingForGuest,
      hasNextPage: !!(data ?? dataForGuest)?.collection?.collectionItems
        ?.pageInfo.hasNextPage,
      collection: (data ?? dataForGuest)?.collection ?? null,
    },
    actions: {
      async onChangeLabel(isFirstLabel, name) {
        if (!currentUser) {
          await refetchForGuest({
            collectionId: router.query.collectionId,
            collectionItemsLabel: isFirstLabel ? undefined : name,
            collectionItemsFirstLabel: isFirstLabel,
          })

          return
        }

        await refetch({
          collectionId: router.query.collectionId,
          collectionItemsLabel: isFirstLabel ? undefined : name,
          collectionItemsFirstLabel: isFirstLabel,
        })
      },
      async onFetchMore(isFirstLabel, name) {
        if (!currentUser) {
          await fetchMoreForGuest({
            variables: {
              collectionId: router.query.collectionId,
              collectionItemsLabel: isFirstLabel ? undefined : name,
              collectionItemsFirstLabel: isFirstLabel,
              pageInput: {
                limit: 12,
                cursor:
                  dataForGuest?.collection?.collectionItems?.pageInfo.endCursor,
              },
            },
          })

          return
        }

        await fetchMore({
          variables: {
            collectionId: router.query.collectionId,
            collectionItemsLabel: isFirstLabel ? undefined : name,
            collectionItemsFirstLabel: isFirstLabel,
            pageInput: {
              limit: 12,
              cursor: data?.collection?.collectionItems?.pageInfo.endCursor,
            },
          },
        })
      },
      async onClickShare() {
        analytics?.track('click_share', {
          applicationKey: config.applicationKey,
          artistId,
          collectionId: router.query.collectionId,
          name: 'collectionStore',
        })
        const url = await getShortLinkUrl()
        await Share.share({
          title: data?.collection?.title,
          text: data?.collection?.description || '',
          url,
          dialogTitle: data?.collection?.title,
        })
          .then(() => {
            analytics?.track('click_shareMethod', {
              applicationKey: config.applicationKey,
              artistId,
              collectionId: router.query.collectionId,
              name: 'storeCollectionDetail',
            })
          })
          .catch(e => showError(e))

        return
      },
    },
  }
})

const QUERY = gql`
  query StoreCollectionDetails(
    $collectionId: ID!
    $collectionItemsLabel: String
    $collectionItemsFirstLabel: Boolean
    $pageInput: PageInput
    $userId: ID
  ) {
    user(id: $userId) {
      id
      displayName
      avatar {
        uri
        smallUri
      }
    }
    userCartCount
    userCarts {
      ...UserCartsConnection
    }
    collection(id: $collectionId) {
      id
      title
      artistId
      isEventPickup
      artist {
        id
        displayName
      }
      isPublishOnStore
      thumbnail {
        uri
        mediumUri
      }
      userCollection(userId: $userId) {
        ogp {
          uri
          smallUri
        }
        itemOwnedCount
        totalOwnedCount
        totalOwnedItemCount @client
        ownedItems {
          itemId
          ownedCount
        }
        id
        userId
      }
      publishedItemCount
      description
      labels {
        name
        id
      }
      collectionItems(
        label: $collectionItemsLabel
        firstLabel: $collectionItemsFirstLabel
        pageInput: $pageInput
        publishedOnly: true
      ) {
        ...StoreCollectionItemsConnection
      }
    }
  }
  ${StoreCollectionItemsConnectionFragmentDoc}
  ${UserCartsConnectionFragmentDoc}
`

const QUERY_FOR_GUEST = gql`
  query StoreCollectionDetailsForGuest(
    $collectionId: ID!
    $collectionItemsLabel: String
    $collectionItemsFirstLabel: Boolean
    $pageInput: PageInput
  ) {
    collection(id: $collectionId) {
      id
      title
      artistId
      isEventPickup
      artist {
        id
        displayName
      }
      isPublishOnStore
      thumbnail {
        uri
        mediumUri
      }
      publishedItemCount
      description
      labels {
        name
        id
      }
      collectionItems(
        label: $collectionItemsLabel
        firstLabel: $collectionItemsFirstLabel
        pageInput: $pageInput
        publishedOnly: true
      ) {
        ...StoreCollectionItemsConnection
      }
    }
  }
  ${StoreCollectionItemsConnectionFragmentDoc}
`
